import { BillingType, CUT_FEE_MAP } from '@consts/company';
import type { RootState } from '@features/redux/store';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useSelector } from 'react-redux';

export const PlanChangeDialog = ({
    currentPlanId,
    targetPlanId,
    stationId,
    open,
    onClose,
    onConfirm,
}: {
    currentPlanId: BillingType;
    targetPlanId: BillingType;
    stationId: number;
    open: boolean;
    onClose: () => void;
    onConfirm: (params: { effectiveDate: string; justification?: string }) => void;
}) => {
    const role = useSelector((state: RootState) => state.auth.Roles.role);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [justification, setJustification] = useState('');
    const [effectiveDate, setEffectiveDate] = useState<Date>(dayjs().toDate());

    const handleRequestChange = async (planId: BillingType) => {
        setLoading(true);
        setError(null);
        // Defer API until parent save. Pass values up.
        try {
            onConfirm({ effectiveDate: dayjs(effectiveDate).format('YYYY-MM-DD'), justification });
            onClose();
        } catch (e: any) {
            setError(e?.message || 'Failed to schedule plan change');
        }
        setLoading(false);
    };

    const isRestricted = targetPlanId === BillingType.NoCutFee && !role.type.finance;

    return (
        <Dialog open={open} onClose={() => onClose()}>
            <DialogTitle variant='h4'>{isRestricted ? 'Request' : 'Schedule'} Plan Change</DialogTitle>

            <DialogContent>
                <Typography>
                    {targetPlanId === BillingType.NoCutFee && !role.type.finance
                        ? `You are requesting to change the billing plan for this system. Our finance team will review, and you
                    will be notified by email once the change is approved or denied.`
                        : targetPlanId === BillingType.NoCutFee
                        ? 'This change will result in no cut fees being billed separately at the end of each month, and requires the use of sheets with cut fee included.'
                        : 'This change will result in cut fees being billed separately at the end of each month, and requires the use of sheets with no cut fee included.'}
                </Typography>

                <Box mt={2}>
                    {/** Can only select dates from first of month to the last day of the month */}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label='Effective Date'
                            value={effectiveDate}
                            onChange={(date) => setEffectiveDate(date!.toDate())}
                            renderInput={(params) => <TextField {...params} />}
                            minDate={dayjs().startOf('month')}
                            maxDate={dayjs().add(2, 'month')}
                        />
                    </LocalizationProvider>
                </Box>

                <Box mt={2}>
                    <Typography variant='body2' color={'black'}>
                        <strong>Note:</strong> requests for billing model changes require 3 business days before the end
                        of the month. Requests made inside of 3 business days may not be processed in time.
                    </Typography>
                </Box>

                {targetPlanId === BillingType.NoCutFee && !role.type.finance && (
                    <TextField
                        value={justification}
                        onChange={(e) => setJustification(e.target.value)}
                        label='Justification (optional)'
                        multiline
                        rows={4}
                        fullWidth
                        margin='normal'
                    />
                )}
            </DialogContent>

            <DialogActions>
                <Button sx={{ textTransform: 'none' }} onClick={() => onClose()}>
                    Cancel
                </Button>

                <Button
                    sx={{ textTransform: 'none' }}
                    onClick={() => handleRequestChange(targetPlanId)}
                    disabled={loading}
                    variant='contained'>
                    {loading
                        ? isRestricted
                            ? 'Requesting...'
                            : 'Scheduling...'
                        : isRestricted
                        ? 'Request Change'
                        : 'Schedule Change'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
